/**
 * Reading and applying the theme, in one place.
 *
 * The same handler had been written out three times, in `EntraShell` twice and
 * again at boot, each setting the attribute and the stored value in its own
 * order. `initTheme` also duplicated the preference fallback.
 *
 * @module components/shared/theme
 */

import type { ThemeName } from "./workspace-types";

const STORAGE_KEY = "socratiq_theme";

function isThemeName(value: string | null): value is ThemeName {
  return value === "light" || value === "dark";
}

/** The theme currently on the document, defaulting to light. */
export function currentTheme(): ThemeName {
  if (typeof document !== "undefined") {
    const value = document.documentElement.getAttribute("data-theme");
    if (isThemeName(value)) {
      return value;
    }
  }
  return "light";
}

/**
 * The theme this browser should open in: the stored choice, or the operating
 * system's preference when there is none.
 *
 * @returns The theme to apply at boot.
 */
export function readTheme(): ThemeName {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeName(stored)) {
      return stored;
    }
  } catch {
    // A blocked store is not a reason to fail the boot; fall through to the
    // operating system's preference.
  }
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

/**
 * Put a theme on the document and remember it.
 *
 * @param theme - The theme to apply.
 */
export function applyTheme(theme: ThemeName, { persist = true } = {}): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", theme);
  }
  if (!persist) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // As above: the preference simply does not persist.
  }
}

/**
 * Apply the theme this browser should open in, without recording it.
 *
 * `readTheme` falls back to the operating system when nothing is stored.
 * Persisting that at boot would freeze the first OS setting a browser ever
 * had: switching the OS to light afterwards would leave the app dark for ever,
 * with nothing the user did to explain it. Only an explicit choice persists.
 */
export function initTheme(): void {
  applyTheme(readTheme(), { persist: false });
}
